/*
 * cx-names-customiser.js -- controller for the four "Your Name on Your ..."
 * customised hard-surface products (luggage, helmet, tiffin box, school
 * bottle). Rendered by snippets/cx-names-customiser.liquid only; shares no
 * state with the other customisers (custom-personalization-*.js).
 *
 * Order pipeline contract (Operations OS, zero changes needed):
 *  - The design is flattened client-side (cx-names-art.js) into ONE
 *    transparent PNG and attached as the "Custom Image" line-item property
 *    via a multipart /cart/add.js, exactly like the live Customise Your Own
 *    Image / QR products. customized-demand.ts prints it as an image, with
 *    its longest side equal to the size in the variant title.
 *  - No other property may have a key that customized-demand.ts reads as a
 *    TEXT order ("Custom Text" / "Font" / "Color"), or the order would be
 *    re-typeset in a fallback font instead of printing this PNG. Hence
 *    "Name" / "Lettering" / "Fill" here.
 *  - No other property VALUE may start with "http" (the image detector
 *    takes the first http-valued property).
 *  - School label pack (kids products): a dedicated variant whose title
 *    reads "... (2 inches each)", added with line quantity = labels x packs.
 *    Sheet generation prints line quantity copies at the variant size, so
 *    every label comes out as its own correctly sized piece.
 */
(function () {
  'use strict';

  var A = window.CXNamesArt;
  if (!A) return;

  var NAME_MAX = 16;
  // Comfortable minimum letter height on a peel-and-stick name patch.
  var MIN_CAP_IN = 0.16;
  var PRINT_MAX_SIDE = 2400; // >= 300 dpi at the largest (6 inch) size
  var PREVIEW_MAX_SIDE = 900;

  function $(root, sel) {
    return root.querySelector(sel);
  }
  function $$(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function el(tag, cls, attrs) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'text') e.textContent = attrs[k];
        else e.setAttribute(k, attrs[k]);
      });
    }
    return e;
  }

  var DEVA = /[\u0900-\u097F]/;

  function cleanName(raw, max, hindi) {
    // Latin letters (incl. accents), digits, space and a few marks. Most
    // vendored fonts are Latin-only; anything else would silently fall
    // back to a system font on the print. Products whose config sets
    // "hindi" also accept Devanagari (only the fonts flagged "deva" can
    // print it, see syncScript below).
    var re = hindi ? /[^A-Za-z0-9À-ÿ\u0900-\u097F\u200C\u200D .&'!\-]/g : /[^A-Za-z0-9À-ÿ .&'!\-]/g;
    return raw.replace(re, '').replace(/\s{2,}/g, ' ').slice(0, max || NAME_MAX);
  }

  function init(root) {
    var cfgEl = $(root, '[data-cx-config]');
    if (!cfgEl) return;
    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return;
    }
    var mock = A.MOCKUPS[cfg.object];
    if (!mock) return;
    var nameMax = cfg.maxLen || NAME_MAX;
    var clean = function (raw) {
      return cleanName(raw, nameMax, !!cfg.hindi);
    };

    var nameInput = $(root, '[data-cx-name]');
    var nameCount = $(root, '[data-cx-name-count]');
    var nameNote = $(root, '[data-cx-name-note]');
    var styleSteps = $$(root, '[data-cx-after-name]');
    var stage = $(root, '[data-cx-stage]');
    var stageArt = $(root, '[data-cx-stage-art]');
    var patchImg = $(root, '[data-cx-patch]');
    var closeup = $(root, '[data-cx-closeup]');
    var closeupImg = $(root, '[data-cx-closeup-img]');
    var zoomBtn = $(root, '[data-cx-zoom]');
    var objSwatchWrap = $(root, '[data-cx-object-swatches]');
    var checkNote = $(root, '[data-cx-check]');
    var legibility = $(root, '[data-cx-legibility]');
    var fitNote = $(root, '[data-cx-fit-note]');
    var fontGrid = $(root, '[data-cx-fonts]');
    var solidWrap = $(root, '[data-cx-solids]');
    var gradWrap = $(root, '[data-cx-gradients]');
    var fillLabel = $(root, '[data-cx-fill-label]');
    var outlineRadios = $$(root, '[data-cx-outline]');
    var contrastTip = $(root, '[data-cx-contrast-tip]');
    var contrastTipText = $(root, '[data-cx-contrast-tip-text]');
    var contrastFix = $(root, '[data-cx-contrast-fix]');
    var iconGrid = $(root, '[data-cx-icons]');
    var formatRadios = $$(root, '[data-cx-format]');
    var singlesBlock = $(root, '[data-cx-singles]');
    var packBlock = $(root, '[data-cx-pack-info]');
    var sizeRadios = $$(root, '[data-cx-size]');
    var packRadio = $(root, '[data-cx-pack-variant]');
    var customSizeInput = $(root, '[data-cx-custom-size]');
    var customSizeError = $(root, '[data-cx-custom-size-error]');
    var customSizeErrorText = $(root, '[data-cx-custom-size-error-text]');
    var customSizeConfirm = $(root, '[data-cx-custom-size-confirm]');
    var priceEl = $(root, '[data-cx-price]');
    var priceNote = $(root, '[data-cx-price-note]');
    var qtyInput = $(root, '[data-cx-qty]');
    var qtyLabel = $(root, '[data-cx-qty-label]');
    var submit = $(root, '[data-cx-submit]');
    var submitText = $(root, '[data-cx-submit-text]');
    var errorWrap = $(root, '[data-cx-error]');
    var errorText = $(root, '[data-cx-error-text]');
    var addedNote = $(root, '[data-cx-added]');

    var state = {
      name: '',
      font: cfg.fonts[0],
      fill: { type: 'solid', name: cfg.defaults.fill, color: hexForSolid(cfg.defaults.fill) },
      outline: 'None',
      icon: null,
      objectColour: mock.swatches[0][1],
      customSize: null,
      closeup: false,
    };

    function hexForSolid(name) {
      for (var i = 0; i < A.SOLIDS.length; i++) if (A.SOLIDS[i][0] === name) return A.SOLIDS[i][1];
      return '#111111';
    }

    /* ---------------- stage + object colour ---------------- */

    stageArt.innerHTML = mock.svg;
    A.applyObjectColour(stage, state.objectColour);

    mock.swatches.forEach(function (sw, i) {
      var b = el('button', 'cx-names__obj-swatch', {
        type: 'button',
        'aria-label': 'Preview on a ' + sw[0].toLowerCase() + ' ' + mock.label,
        title: sw[0],
        'aria-pressed': i === 0 ? 'true' : 'false',
      });
      b.style.background = sw[1];
      b.addEventListener('click', function () {
        state.objectColour = sw[1];
        A.applyObjectColour(stage, sw[1]);
        $$(objSwatchWrap, 'button').forEach(function (x) {
          x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
        });
        syncContrastTip();
        syncCloseupBg();
      });
      objSwatchWrap.appendChild(b);
    });

    if (zoomBtn) {
      zoomBtn.addEventListener('click', function () {
        state.closeup = !state.closeup;
        root.toggleAttribute('data-cx-closeup-on', state.closeup);
        zoomBtn.setAttribute('aria-pressed', state.closeup ? 'true' : 'false');
        zoomBtn.textContent = state.closeup ? 'Show on ' + mock.label : 'Close-up';
      });
    }

    function syncCloseupBg() {
      if (closeup) A.applyObjectColour(closeup, state.objectColour);
    }
    syncCloseupBg();

    /* ---------------- font tiles ---------------- */

    var fontTiles = cfg.fonts.map(function (f, i) {
      var b = el('button', 'cx-names__font', {
        type: 'button',
        'aria-pressed': i === 0 ? 'true' : 'false',
        'aria-label': f.label,
      });
      var sample = el('span', 'cx-names__font-sample');
      sample.style.fontFamily = "'" + f.family + "'";
      var cap = el('span', 'cx-names__font-name', { text: f.label });
      b.appendChild(sample);
      b.appendChild(cap);
      b.addEventListener('click', function () {
        state.font = f;
        fontTiles.forEach(function (t) {
          t.btn.setAttribute('aria-pressed', t.btn === b ? 'true' : 'false');
        });
        schedule();
      });
      fontGrid.appendChild(b);
      return { btn: b, sample: sample, font: f };
    });

    // Hindi names: only fonts flagged "deva" carry Devanagari glyphs, so the
    // others are switched off while the name contains any, and the choice
    // moves to the first Hindi font.
    function syncScript() {
      if (!cfg.hindi) return;
      var deva = DEVA.test(state.name || '');
      fontTiles.forEach(function (t) {
        t.btn.disabled = deva && !t.font.deva;
      });
      if (deva && !state.font.deva) {
        var f = cfg.fonts.filter(function (x) {
          return x.deva;
        })[0];
        if (f) {
          state.font = f;
          fontTiles.forEach(function (t) {
            t.btn.setAttribute('aria-pressed', t.font === f ? 'true' : 'false');
          });
        }
      }
      fitFontTiles();
    }

    // Probe-and-scale (see Your Name on Laptop): one measurement at a fixed
    // probe size gives the exact font-size that fills the tile width.
    function fitFontTiles() {
      var text = state.name || cfg.defaults.sampleName;
      fontTiles.forEach(function (t) {
        // a font switched off for a Hindi name keeps showing the sample name
        t.sample.textContent = t.btn.disabled ? cfg.defaults.sampleName : text;
        var avail = t.btn.clientWidth - 16;
        if (avail <= 0) return;
        t.sample.style.fontSize = '40px';
        var w = t.sample.getBoundingClientRect().width || 1;
        var size = Math.max(11, Math.min(30, 40 * (avail / w)));
        t.sample.style.fontSize = size + 'px';
      });
    }

    /* ---------------- colour swatches ---------------- */

    function fillSwatch(wrap, name, bg, fill) {
      var b = el('button', 'cx-names__swatch', { type: 'button', 'aria-label': name, title: name, 'aria-pressed': 'false' });
      b.style.background = bg;
      if (name === 'White') b.classList.add('cx-names__swatch--light');
      b.addEventListener('click', function () {
        state.fill = fill;
        syncFillButtons();
        schedule();
      });
      b._cxFillName = name;
      wrap.appendChild(b);
    }

    A.SOLIDS.forEach(function (s) {
      fillSwatch(solidWrap, s[0], s[1], { type: 'solid', name: s[0], color: s[1] });
    });
    A.GRADIENTS.forEach(function (g) {
      fillSwatch(gradWrap, g[0], 'linear-gradient(90deg,' + g[1].join(',') + ')', { type: 'gradient', name: g[0], stops: g[1] });
    });

    function fillDisplayName() {
      return state.fill.type === 'gradient' ? state.fill.name + ' gradient' : state.fill.name;
    }

    function syncFillButtons() {
      $$(root, '.cx-names__swatch').forEach(function (b) {
        b.setAttribute('aria-pressed', b._cxFillName === state.fill.name ? 'true' : 'false');
      });
      if (fillLabel) fillLabel.textContent = fillDisplayName();
    }
    syncFillButtons();

    /* ---------------- outline ---------------- */

    function outlineHex() {
      if (state.outline === 'White') return '#ffffff';
      if (state.outline === 'Black') return '#111111';
      return null;
    }

    outlineRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (r.checked) {
          state.outline = r.value;
          schedule();
        }
      });
    });

    function setOutline(v) {
      state.outline = v;
      outlineRadios.forEach(function (r) {
        r.checked = r.value === v;
      });
      schedule();
    }

    // A dark name on a dark helmet (or a light one on a light lunch box)
    // disappears -- suggest the outline that fixes it, one tap.
    function fillAverageHex() {
      if (state.fill.type === 'solid') return state.fill.color;
      var stops = state.fill.stops;
      var r = 0;
      var g = 0;
      var b = 0;
      stops.forEach(function (h) {
        var n = parseInt(h.slice(1), 16);
        r += (n >> 16) & 255;
        g += (n >> 8) & 255;
        b += n & 255;
      });
      var k = stops.length;
      return '#' + ((1 << 24) | (Math.round(r / k) << 16) | (Math.round(g / k) << 8) | Math.round(b / k)).toString(16).slice(1);
    }

    var suggestedOutline = null;
    function syncContrastTip() {
      if (!contrastTip) return;
      var ratio = A.contrast(fillAverageHex(), state.objectColour);
      var objDark = A.contrast(state.objectColour, '#000000') < A.contrast(state.objectColour, '#ffffff');
      suggestedOutline = objDark ? 'White' : 'Black';
      var show = !!state.name && state.outline === 'None' && ratio < 2;
      contrastTip.hidden = !show;
      if (show) {
        contrastTipText.textContent =
          fillDisplayName() + ' is hard to see on a ' + (objDark ? 'dark' : 'light') + ' ' + mock.label + '.';
        contrastFix.textContent = 'Add a ' + suggestedOutline.toLowerCase() + ' outline';
      }
    }
    if (contrastFix) {
      contrastFix.addEventListener('click', function () {
        if (suggestedOutline) setOutline(suggestedOutline);
      });
    }

    /* ---------------- icons ---------------- */

    function iconButton(key, label) {
      var b = el('button', 'cx-names__icon', { type: 'button', 'aria-label': label, title: label, 'aria-pressed': 'false' });
      if (!key) {
        b.appendChild(el('span', 'cx-names__icon-none', { text: 'None' }));
      } else {
        var c = el('canvas', '', { width: '64', height: '64' });
        A.drawIcon(c.getContext('2d'), key, 6, 6, 52, '#1a1a1a');
        b.appendChild(c);
      }
      b.addEventListener('click', function () {
        state.icon = key;
        syncIconButtons();
        schedule();
      });
      b._cxIcon = key;
      iconGrid.appendChild(b);
    }
    iconButton(null, 'No icon');
    cfg.icons.forEach(function (k) {
      if (A.ICONS[k]) iconButton(k, A.ICONS[k].label);
    });
    function syncIconButtons() {
      $$(iconGrid, '.cx-names__icon').forEach(function (b) {
        b.setAttribute('aria-pressed', b._cxIcon === state.icon ? 'true' : 'false');
      });
    }
    syncIconButtons();

    /* ---------------- sizes / format ---------------- */

    function isPack() {
      return !!(packRadio && packRadio.checked);
    }

    function packCount() {
      return packRadio ? parseInt(packRadio.dataset.packCount, 10) || 8 : 1;
    }

    function selectedSizeRadio() {
      if (isPack()) return packRadio;
      return sizeRadios.filter(function (r) {
        return r.checked;
      })[0];
    }

    function selectedSizeIn() {
      if (!isPack() && state.customSize) return state.customSize;
      var r = selectedSizeRadio();
      return r ? parseFloat(r.dataset.sizeIn) : cfg.defaults.sizeIn;
    }

    formatRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        var pack = r.checked && r.value === 'pack';
        if (packRadio) packRadio.checked = pack;
        if (!pack && !selectedSizeRadio()) {
          var def = sizeRadios.filter(function (s) {
            return parseFloat(s.dataset.sizeIn) === cfg.defaults.sizeIn;
          })[0];
          if (def) def.checked = true;
        }
        syncFormat();
        schedule();
      });
    });

    function syncFormat() {
      var pack = isPack();
      if (singlesBlock) singlesBlock.hidden = pack;
      if (packBlock) packBlock.hidden = !pack;
      if (qtyLabel) qtyLabel.textContent = pack ? 'Number of packs' : 'Quantity';
      syncPrice();
    }

    sizeRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (root.dataset.cxSyncingCustom === 'true') return;
        // A real click on a standard size clears any typed custom size.
        if (customSizeInput) customSizeInput.value = '';
        state.customSize = null;
        clearCustomSizeError();
        if (customSizeConfirm) customSizeConfirm.hidden = true;
        sizeRadios.forEach(function (s) {
          s.removeAttribute('data-custom-size-driven');
        });
        schedule();
      });
    });

    /* custom size: same contract as custom-personalization-sizes.liquid --
       pick the smallest standard size at or above it for pricing, send the
       exact value as "Custom Size (inches)" (read by Operations OS). */
    function clearCustomSizeError() {
      if (customSizeError) customSizeError.hidden = true;
      root.removeAttribute('data-cx-size-error');
    }
    function showCustomSizeError(msg) {
      if (customSizeErrorText) customSizeErrorText.textContent = msg;
      if (customSizeError) customSizeError.hidden = false;
      root.setAttribute('data-cx-size-error', '');
    }
    function validateCustomSize(raw) {
      clearCustomSizeError();
      if (!/^\d+(\.\d)?$/.test(raw)) {
        showCustomSizeError('Enter a size in inches, up to 1 decimal place (e.g. 4.5).');
        return null;
      }
      var v = Math.round(parseFloat(raw) * 10) / 10;
      var min = parseFloat(customSizeInput.dataset.minSize);
      var max = parseFloat(customSizeInput.dataset.maxSize);
      if (v < min || v > max) {
        showCustomSizeError('Size must be between ' + min + '" and ' + max + '".');
        return null;
      }
      return v;
    }
    if (customSizeInput) {
      customSizeInput.addEventListener('input', function () {
        var raw = customSizeInput.value.trim();
        if (!raw) clearCustomSizeError();
        else validateCustomSize(raw);
        syncSubmit();
      });
      customSizeInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') customSizeInput.blur();
      });
      customSizeInput.addEventListener('blur', function () {
        var raw = customSizeInput.value.trim();
        state.customSize = null;
        if (customSizeConfirm) customSizeConfirm.hidden = true;
        sizeRadios.forEach(function (s) {
          s.removeAttribute('data-custom-size-driven');
        });
        if (!raw) {
          clearCustomSizeError();
          schedule();
          return;
        }
        var v = validateCustomSize(raw);
        if (v === null) {
          syncSubmit();
          return;
        }
        var best = null;
        sizeRadios.forEach(function (s) {
          var size = parseFloat(s.dataset.sizeIn);
          if (size + 0.001 >= v && (!best || size < parseFloat(best.dataset.sizeIn))) best = s;
        });
        if (!best) {
          showCustomSizeError('Size must be between ' + customSizeInput.dataset.minSize + '" and ' + customSizeInput.dataset.maxSize + '".');
          syncSubmit();
          return;
        }
        state.customSize = v;
        root.dataset.cxSyncingCustom = 'true';
        best.checked = true;
        best.setAttribute('data-custom-size-driven', '');
        best.dispatchEvent(new Event('change', { bubbles: true }));
        delete root.dataset.cxSyncingCustom;
        if (customSizeConfirm) {
          customSizeConfirm.textContent = "We'll size your design to exactly " + v + '".';
          customSizeConfirm.hidden = false;
        }
        schedule();
      });
    }

    /* ---------------- price ---------------- */

    function syncPrice() {
      var native = document.querySelector('[id^="price-"]');
      if (native) native.style.display = 'none';
      var r = selectedSizeRadio();
      if (!r) {
        priceEl.hidden = true;
        priceNote.hidden = true;
        return;
      }
      if (isPack()) {
        priceEl.textContent = r.dataset.packTotal;
        priceNote.textContent = r.dataset.packNote;
        priceNote.hidden = false;
      } else {
        priceEl.textContent = r.dataset.price;
        priceNote.hidden = true;
      }
      priceEl.hidden = false;
    }

    /* ---------------- render ---------------- */

    var timer = null;
    var renderSeq = 0;
    var lastPreview = null;

    function schedule() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(render, 70);
      syncSubmit();
      syncPrice();
    }

    function designOpts(maxSide) {
      return {
        text: state.name,
        fontFamily: state.font.family,
        fill: state.fill,
        outline: outlineHex(),
        icon: state.icon,
        maxSide: maxSide,
      };
    }

    function render() {
      var seq = ++renderSeq;
      var hasName = !!state.name;
      root.toggleAttribute('data-cx-has-name', hasName);
      styleSteps.forEach(function (s) {
        s.hidden = !hasName;
      });
      fitFontTiles();
      syncContrastTip();
      if (checkNote) checkNote.hidden = !hasName;
      if (!hasName) {
        patchImg.hidden = true;
        if (closeupImg) closeupImg.hidden = true;
        if (legibility) legibility.hidden = true;
        if (fitNote) fitNote.hidden = true;
        lastPreview = null;
        return;
      }
      A.compose(designOpts(PREVIEW_MAX_SIDE)).then(function (res) {
        if (seq !== renderSeq) return;
        lastPreview = res;
        var url = res.canvas.toDataURL('image/png');
        var box = A.patchBox(cfg.object, selectedSizeIn(), res.canvas.width, res.canvas.height);
        patchImg.src = url;
        patchImg.style.left = box.left + '%';
        patchImg.style.top = box.top + '%';
        patchImg.style.width = box.width + '%';
        patchImg.style.height = box.height + '%';
        patchImg.hidden = false;
        if (closeupImg) {
          closeupImg.src = url;
          closeupImg.hidden = false;
        }
        if (fitNote) {
          fitNote.hidden = !box.clamped;
          if (box.clamped) {
            fitNote.textContent =
              'At ' + selectedSizeIn() + '" your patch is wider than most ' + mock.label + 's, so it is shown smaller here. It will curve around the sides.';
          }
        }
        syncLegibility(res);
      });
    }

    function syncLegibility(res) {
      if (!legibility) return;
      var sizeIn = selectedSizeIn();
      var capIn = res.capRatio * sizeIn;
      if (!res.capRatio || capIn >= MIN_CAP_IN) {
        legibility.hidden = true;
        return;
      }
      var mm = Math.max(1, Math.round(capIn * 25.4 * 10) / 10);
      var needIn = MIN_CAP_IN / res.capRatio;
      var rec = null;
      sizeRadios.forEach(function (s) {
        var v = parseFloat(s.dataset.sizeIn);
        if (v + 0.001 >= needIn && (rec === null || v < rec)) rec = v;
      });
      var msg = 'Heads up: at ' + sizeIn + '" the letters will be about ' + mm + ' mm tall.';
      if (isPack()) msg += ' A shorter name (first name only) reads better on the small labels.';
      else if (rec) msg += ' For easy reading, choose ' + rec + '" or bigger, or a shorter name.';
      else msg += ' A shorter name will read better.';
      legibility.textContent = msg;
      legibility.hidden = false;
    }

    nameInput.setAttribute('maxlength', String(nameMax));
    nameInput.addEventListener('input', function () {
      var cleaned = clean(nameInput.value);
      var removed = cleaned !== nameInput.value.slice(0, nameMax) || nameInput.value.length > nameMax;
      if (cleaned !== nameInput.value) {
        var pos = Math.min(cleaned.length, nameInput.selectionStart || cleaned.length);
        nameInput.value = cleaned;
        try {
          nameInput.setSelectionRange(pos, pos);
        } catch (e) {}
      }
      if (nameNote) nameNote.hidden = !removed;
      state.name = cleaned.trim();
      syncScript();
      if (nameCount) nameCount.textContent = cleaned.length + '/' + nameMax;
      if (addedNote) addedNote.hidden = true;
      schedule();
    });

    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', function () {
        fitFontTiles();
      });
    }
    // Warm every font in this group so the tiles preview correctly.
    cfg.fonts.forEach(function (f) {
      A.loadFont(f.family, cfg.defaults.sampleName);
    });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fitFontTiles, 120);
    });

    /* ---------------- cart ---------------- */

    function syncSubmit() {
      var r = selectedSizeRadio();
      if (!state.name) {
        submit.disabled = true;
        submitText.textContent = 'Type a name first';
      } else if (root.hasAttribute('data-cx-size-error')) {
        submit.disabled = true;
        submitText.textContent = 'Fix custom size';
      } else if (!r) {
        submit.disabled = true;
        submitText.textContent = 'Select a size';
      } else {
        submit.disabled = false;
        submitText.textContent = 'Add to cart';
      }
    }

    function showError(m) {
      errorText.textContent = m;
      errorWrap.hidden = false;
    }
    function clearError() {
      errorWrap.hidden = true;
      errorText.textContent = '';
    }

    function setLoading(on) {
      submit.toggleAttribute('aria-disabled', on);
      submit.classList.toggle('loading', on);
      var sp = submit.querySelector('.loading__spinner');
      if (sp) sp.classList.toggle('hidden', !on);
    }

    function lineProperties() {
      var props = {
        Name: state.name,
        Lettering: state.font.label,
        Fill: fillDisplayName(),
      };
      if (state.outline !== 'None') props.Outline = state.outline;
      if (state.icon) props.Icon = A.ICONS[state.icon].label;
      if (isPack()) props.Pack = packCount() + ' labels';
      if (!isPack() && state.customSize) props['Custom Size (inches)'] = String(state.customSize);
      // Hidden (underscore) copy of the design so the cart's Edit link can
      // rebuild it -- JSON starts with "{", never "http".
      props._cx_design = JSON.stringify({
        n: state.name,
        f: state.font.slug,
        fill: state.fill.name,
        o: state.outline,
        i: state.icon,
      });
      return props;
    }

    submit.addEventListener('click', function () {
      clearError();
      if (addedNote) addedNote.hidden = true;
      var r = selectedSizeRadio();
      if (!state.name || !r) return;
      var qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      var lineQty = isPack() ? qty * packCount() : qty;
      setLoading(true);

      A.compose(designOpts(PRINT_MAX_SIDE))
        .then(function (res) {
          return new Promise(function (resolve, reject) {
            res.canvas.toBlob(function (blob) {
              if (!blob) reject(new Error('Could not create your design. Please try again.'));
              else resolve(blob);
            }, 'image/png');
          });
        })
        .then(function (blob) {
          var fd = new FormData();
          fd.append('id', r.value);
          fd.append('quantity', String(lineQty));
          fd.append('properties[Custom Image]', new File([blob], 'name-patch.png', { type: 'image/png' }));
          var props = lineProperties();
          Object.keys(props).forEach(function (k) {
            fd.append('properties[' + k + ']', props[k]);
          });
          fd.append('sections', 'cart-drawer,cart-icon-bubble');
          fd.append('sections_url', window.location.pathname);
          return fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: fd,
          }).then(function (res) {
            return res.json();
          });
        })
        .then(function (resp) {
          if (resp.status) throw new Error(resp.description || resp.message || 'Could not add to cart.');
          var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cart && typeof cart.renderContents === 'function' && resp.sections) cart.renderContents(resp);
          var drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.classList.remove('is-empty');
          var items = document.querySelector('cart-drawer-items');
          if (items) items.classList.remove('is-empty');
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cx-names', productVariantId: r.value, cartData: resp });
          }
          // Keep the design on screen: adding the same name in a second
          // size (or a pack) is one tap away.
          if (addedNote) addedNote.hidden = false;
          qtyInput.value = 1;
        })
        .catch(function (err) {
          showError((err && err.message) || 'Something went wrong adding this to your cart. Please try again.');
        })
        .finally(function () {
          setLoading(false);
        });
    });

    $$(root, '[data-cx-qty-step]').forEach(function (b) {
      b.addEventListener('click', function () {
        var d = parseInt(b.dataset.cxQtyStep, 10);
        qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + d);
      });
    });

    /* ---------------- edit from cart ---------------- */

    // The shared cart "Edit" link sends ?edit_variant=&edit_qty=&edit_image=
    // for any line with a Custom Image. Rebuild the design from the line's
    // hidden _cx_design property.
    function restoreFromCart() {
      var params = new URLSearchParams(window.location.search);
      var editVariant = params.get('edit_variant');
      if (!editVariant) return;
      var radio = sizeRadios.concat(packRadio ? [packRadio] : []).filter(function (x) {
        return x.value === editVariant;
      })[0];
      if (radio) {
        radio.checked = true;
        formatRadios.forEach(function (f) {
          f.checked = f.value === (radio === packRadio ? 'pack' : 'single');
        });
        syncFormat();
      }
      var editQty = parseInt(params.get('edit_qty'), 10);
      if (editQty) qtyInput.value = radio === packRadio ? Math.max(1, Math.round(editQty / packCount())) : editQty;
      var editImage = params.get('edit_image');
      if (!editImage) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var line = (cart.items || []).filter(function (it) {
            return it.properties && it.properties['Custom Image'] === editImage && it.properties._cx_design;
          })[0];
          if (!line) return;
          var d = JSON.parse(line.properties._cx_design);
          var f = cfg.fonts.filter(function (x) {
            return x.slug === d.f;
          })[0];
          if (f) {
            state.font = f;
            fontTiles.forEach(function (t) {
              t.btn.setAttribute('aria-pressed', t.font === f ? 'true' : 'false');
            });
          }
          var fill = null;
          A.SOLIDS.forEach(function (s) {
            if (s[0] === d.fill) fill = { type: 'solid', name: s[0], color: s[1] };
          });
          A.GRADIENTS.forEach(function (g) {
            if (g[0] === d.fill) fill = { type: 'gradient', name: g[0], stops: g[1] };
          });
          if (fill) state.fill = fill;
          syncFillButtons();
          state.icon = d.i && A.ICONS[d.i] ? d.i : null;
          syncIconButtons();
          if (d.o) setOutline(d.o);
          nameInput.value = clean(d.n || '');
          nameInput.dispatchEvent(new Event('input'));
        })
        .catch(function () {});
    }

    /* ---------------- boot ---------------- */

    syncFormat();
    schedule();
    restoreFromCart();
  }

  function boot() {
    $$(document, '[data-cx-names]').forEach(function (root) {
      if (root.dataset.cxBound) return;
      root.dataset.cxBound = 'true';
      init(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
